import type { DetectionBox, DetectionCategory, MemoryProfile, OcrLine, Rect } from "@aimosaic/core";
import { TEXT_CATEGORIES } from "@aimosaic/core";

export function recoverOcrTextHintBoxes(
  lines: OcrLine[],
  hintBoxes: DetectionBox[],
  enabledCategories: readonly DetectionCategory[],
  memory: MemoryProfile = {}
): DetectionBox[] {
  const enabled = new Set(enabledCategories);
  const ignoredTexts = new Set((memory.ignoredTexts ?? []).map(normalizeMemoryText));
  const recovered: DetectionBox[] = [];

  for (const hint of hintBoxes) {
    if (hint.source !== "local-vlm" || !TEXT_CATEGORIES.has(hint.category) || !enabled.has(hint.category)) continue;
    if (!hint.text || ignoredTexts.has(normalizeMemoryText(hint.text))) continue;
    const hintText = hint.text;
    if (recovered.some((box) => box.category === hint.category && normalizeComparable(box.text ?? "") === normalizeComparable(hintText))) continue;

    const match = findHintInLines(lines, hintText);
    if (!match) continue;
    recovered.push({
      id: `ocr-hint-${hint.category}-${recovered.length}-${match.lineIndex}`,
      category: hint.category,
      bbox: lineRangeToRect(match.line.bbox, match.line.text.length, match.start, match.end),
      confidence: Math.max(0.9, hint.confidence - 0.02),
      source: "local-ocr",
      text: normalizeHintText(hint.category, hintText),
      reason: "vlm text OCR alignment",
      requiresReview: true,
      action: "mask"
    });
  }

  return recovered;
}

function findHintInLines(lines: OcrLine[], hintText: string): { line: OcrLine; lineIndex: number; start: number; end: number } | null {
  const target = normalizeComparable(hintText);
  if (target.length < 4) return null;

  for (const [lineIndex, line] of lines.entries()) {
    const indexed = normalizeComparableWithIndexes(line.text);
    const compactIndex = indexed.text.indexOf(target);
    if (compactIndex < 0) continue;
    const start = indexed.indexes[compactIndex];
    const end = indexed.indexes[compactIndex + target.length - 1] + 1;
    return { line, lineIndex, start, end };
  }

  return null;
}

function normalizeHintText(category: DetectionCategory, text: string): string {
  const normalized = normalizeDashes(text.trim());
  return category === "phone" ? normalized : normalized.replace(/\s+/g, "");
}

function normalizeComparable(text: string): string {
  return normalizeComparableWithIndexes(text).text;
}

function normalizeComparableWithIndexes(text: string): { text: string; indexes: number[] } {
  let normalized = "";
  const indexes: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    const char = normalizeDashes(text[index]).toLowerCase();
    if (!/[a-z0-9]/.test(char)) continue;
    normalized += char;
    indexes.push(index);
  }
  return { text: normalized, indexes };
}

function normalizeMemoryText(text: string): string {
  return normalizeDashes(text).trim().toLowerCase();
}

function normalizeDashes(text: string): string {
  return text.replace(/[\u2010-\u2015\u2212]/g, "-");
}

function lineRangeToRect(lineBox: Rect, textLength: number, start: number, end: number): Rect {
  const safeLength = Math.max(1, textLength);
  return {
    x: lineBox.x + lineBox.width * (start / safeLength),
    y: lineBox.y,
    width: lineBox.width * ((end - start) / safeLength),
    height: lineBox.height
  };
}
